<template>
  <div class="space-y-4">
    <BaseCard>
      <BaseCardHeader>
        <div class="flex justify-between items-baseline -mr-2">
          <h5 class="font-light">Attachments</h5>
          <BaseIconButton v-if="!edit" icon="edit" brand @click="edit = !edit" />
          <template v-else>
            <div v-if="!updating" class="space-x-2 justify-end">
              <BaseIconButton icon="times" silver @click="doRevert" />
              <BaseIconButton green icon="check" @click="doSubmit" />
            </div>
            <div v-else>
              <Loader small />
            </div>
          </template>
        </div>
      </BaseCardHeader>
      <BaseCardBody>
        <form data-vv-scope="accountAttachments" @submit.prevent>
          <div class="-m-4">
            <BaseList nested>
              <BaseListHeader>
                <div class="flex -mx-4">
                  <div class="flex-1 px-4">File</div>
                </div>
              </BaseListHeader>
              <BaseListBody>
                <BaseListItem v-for="(attachment, idx) in localAttachments" :key="idx">
                  <div class="flex -mx-4 items-baseline">
                    <div class="flex-1 px-4">
                      <input
                        v-if="edit"
                        type="text"
                        class="input"
                        :disabled="updating"
                        v-model="attachment.name"
                      />
                      <a v-else :href="attachment.url" target="_blank">{{ attachment.name }}</a>
                    </div>
                    <div v-if="edit && !updating" class="flex-none px-2">
                      <BaseIconButton icon="trash" red @click="removeLocalAttachment(attachment)" />
                    </div>
                  </div>
                </BaseListItem>
                <BaseListItem v-if="localAttachments.length === 0">
                  <div class="flex items-baseline -mx-4">
                    <div class="flex-1 px-4 text-center">Nothing to see yet!</div>
                  </div>
                </BaseListItem>
              </BaseListBody>
            </BaseList>
          </div>
        </form>
      </BaseCardBody>
      <BaseCardFooter v-if="edit && !updating">
        <div class="flex space-x-2">
          <label class="ml-auto">
            <span class="btn btn-success">Add Files</span>
            <input
              ref="fileInput"
              type="file"
              multiple
              @change="handleFileAttachments($event.target.files)"
              class="hidden"
            />
          </label>
        </div>
      </BaseCardFooter>
    </BaseCard>

    <BaseCard v-if="edit && newAttachments.length > 0 && !updating">
      <BaseCardHeader>
        <h5 class="font-light py-1">Upload List</h5>
      </BaseCardHeader>
      <BaseCardBody>
        <div class="-m-4">
          <BaseList nested>
            <BaseListHeader>
              <div class="flex -mx-4">
                <div class="w-64 px-4">Name</div>
                <div class="w-64 px-4">Type</div>
                <div class="w-24 px-4">Size</div>
              </div>
            </BaseListHeader>
            <BaseListBody>
              <BaseListItem v-for="(attachment, idx) in newAttachments" :key="idx">
                <div class="flex -mx-4 items-baseline">
                  <div class="w-64 px-4">
                    <input
                      type="text"
                      class="input"
                      :disabled="uploadingIds.length > 0"
                      v-model="attachment.name"
                    />
                  </div>
                  <div class="w-64 px-4">
                    {{ attachment.file.type }}
                  </div>
                  <div class="w-24 px-4">{{ (attachment.file.size / 1024).toFixed(2) }} KB</div>
                  <div class="flex-1 px-4 text-right">
                    <BaseBadge
                      v-if="attachment.status !== 'inactive'"
                      :brand="attachment.status !== 'failed'"
                      :red="attachment.status === 'failed'"
                    >
                      <span class="uppercase">{{ attachment.status }}</span>
                      <span v-if="attachment.status === 'uploading'">
                        {{ attachment.progress }}%</span
                      >
                    </BaseBadge>
                    <ul v-if="attachment.errors" class="mt-2">
                      <li v-for="(msg, msgIdx) in attachment.errors" :key="msgIdx">
                        {{ msg }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="uploadingIds.length === 0" class="flex-none px-2">
                    <BaseIconButton icon="trash" red @click="removeNewAttachment(attachment)" />
                  </div>
                </div>
              </BaseListItem>
            </BaseListBody>
          </BaseList>
        </div>
      </BaseCardBody>
      <BaseCardFooter>
        <div class="flex">
          <button
            v-if="newAttachments.length > 0"
            class="btn btn-brand ml-auto"
            :disabled="uploadingIds.length > 0"
            @click="handleUpload"
          >
            Upload
          </button>
        </div>
      </BaseCardFooter>
    </BaseCard>
  </div>
</template>

<script>
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

const allowedMimeTypes = {
  'application/pdf': 'pdf',
  'text/plain': 'txt',
  'image/png': 'png',
  'image/jpeg': 'jpeg',
  'image/bmp': 'bmp',
  'video/mp4': 'mp4',
  'audio/mpeg3': 'mp3',
};

export default {
  name: 'Attachments',
  mixins: [veeValidateErrorsMixin],
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    edit: false,
    updating: false,
    uploadingIds: [],
    localAttachments: [],
    newAttachments: [],
  }),
  watch: {
    $currentAccount: {
      handler(account) {
        const { media } = account;
        if (media && Array.isArray(media)) this.cloneAttachments(media);
      },
      deep: true,
    },
    uploadingIds: {
      handler(arr) {
        if (arr.length === 0) {
          this.doRevert();
        }
      },
      deep: true,
    },
  },
  created() {
    const { media = [] } = this.$currentAccount || {};
    this.cloneAttachments(media || []);
  },
  methods: {
    cloneAttachments(media = []) {
      this.localAttachments = [
        ...JSON.parse(JSON.stringify(media.filter((x) => x.collection === 'attachments'))),
      ];
    },
    handleFileAttachments(fileList) {
      Array.from(fileList).forEach((file) => {
        const id = Math.random().toString(36).substr(2);

        const valid = allowedMimeTypes[file.type] !== undefined;

        this.newAttachments.push({
          id,
          name: file.name,
          file,
          status: 'inactive',
          progress: 0,
          valid,
          errors: null,
        });
      });
      this.$refs.fileInput.value = null;
    },
    removeNewAttachment(attachment) {
      const index = this.newAttachments.findIndex((x) => x.id === attachment.id);
      const exists = index > -1;
      if (exists) {
        this.newAttachments.splice(index, 1);
      }
    },
    getFileType(file) {
      const { type } = file;
      return allowedMimeTypes[type] || type;
    },
    /* eslint-disable no-param-reassign */
    handleUpload() {
      this.newAttachments.forEach((attachment) => {
        if (!attachment.valid) return;

        const config = {
          onUploadProgress(progressEvent) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            attachment.progress = progress;
            if (progress === 100) {
              attachment.status = 'storing';
            }
          },
        };

        const success = () => {
          this.removeNewAttachment(attachment);
          this.removeUploadingId(attachment.id);
        };

        const error = (errors) => {
          attachment.status = 'failed';
          attachment.errors = errors;
        };

        const data = new FormData();
        attachment.status = 'uploading';
        data.append('file', attachment.file);
        data.append('name', attachment.name);

        this.uploadingIds.push(attachment.id);

        this.upload(data, config, success, error);
      });
    },
    /* eslint-enable no-param-reassign */
    async upload(data, config, success, failed) {
      try {
        const { dispatch } = this.$store;
        const { accountId } = this;

        await dispatch('ui/accounts/uploadAccountAttachment', { accountId, data, config });

        success();
      } catch (error) {
        const { data: errorData = {} } = error || {};
        const { errors = {} } = errorData;
        const errorArr = Object.keys(errors).reduce((acc, cur) => [...acc, ...errors[cur]], []);
        failed(Array.from(errorArr));
      }
    },
    removeUploadingId(id) {
      const index = this.uploadingIds.indexOf(id);
      const exists = index > -1;
      if (exists) {
        this.uploadingIds.splice(index, 1);
      }
    },
    removeLocalAttachment(attachment) {
      const index = this.localAttachments.findIndex((x) => x.id === attachment.id);
      const exists = index > -1;
      if (exists) {
        this.localAttachments.splice(index, 1);
      }
    },
    doRevert() {
      const { media } = this.$currentAccount;
      if (media && Array.isArray(media)) this.cloneAttachments(media);
      this.newAttachments = [];
      this.edit = false;
    },
    async doSubmit() {
      const { dispatch } = this.$store;
      const { accountId } = this;
      const updatedAttachments = {
        attachments: [...this.localAttachments.map(({ id, name }) => ({ id, name }))],
      };

      this.updating = true;

      const dispatchWithErrorHandling = this.handleFormErrors(dispatch);

      await dispatchWithErrorHandling('ui/accounts/updateAccountAttachments', {
        accountId,
        updatedAttachments,
      });

      this.updating = false;
    },
    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            this.edit = false;
          })
          .catch((error) => {
            const { status = null, data } = error;
            if (status === HTTP_UNPROCESSABLE_ENTITY) {
              this.doInsertVeeValidateErrors(data.errors, 'accountTags');
            } else {
              this.errorMessage = 'Something went wrong! Please try again';
            }
          });
    },
  },
};
</script>
